import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  TablePagination,
} from "@mui/material";
import api from "../../utils/api";
import UserActions from "./UserActions";
import UserFormDialog from "./UserFormDialog";
import UserDeleteDialog from "./UserDeleteDialog";
import BulkInviteButton from "./BulkInviteButton";
import UserResetPasswordDialog from "./UserResetPasswordDialog";
import UserResetSuccessDialog from "./UserResetSuccessDialog";

const PropertyAccountsTable = () => {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState("add");
  const [formData, setFormData] = useState({});
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [userToReset, setUserToReset] = useState(null);
  const [resetResult, setResetResult] = useState(null);

  // pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  useEffect(() => {
    loadUsers();
    loadRoles();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await api.get("/PropertyAccounts");

      const userMap = new Map();

      response.data.forEach((row) => {
        if (!userMap.has(row.Username)) {
          userMap.set(row.Username, {
            Username: row.Username,
            UserID: row.UserID,
            Name: row.Name,
            Lastname: row.Lastname,
            Email: row.Email,
            Status: row.status || row.Status,
            AssociatedAccounts: Array.isArray(row.AssociatedAccounts)
              ? row.AssociatedAccounts
              : row.AssociatedAccounts
              ? String(row.AssociatedAccounts).split(",").map((a) => a.trim())
              : [],
            Roles: [],
          });
        }

        userMap.get(row.Username).Roles.push({
          RoleID: row.RoleID,
          RoleName: row.RoleName,
        });
      });

      setUsers(Array.from(userMap.values()));
    } catch (err) {
      console.error("Failed to fetch users", err);
    }
  };

  const filteredUsers = users
    .filter((user) => user.Status?.toLowerCase() === "active")
    .filter((user) =>
      ["Username", "Name", "Lastname", "Email"]
        .map((key) => user[key]?.toLowerCase())
        .some((value) => value && value.includes(searchTerm.toLowerCase()))
    );

  // users shown on the current page
  const paginatedUsers = filteredUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // ✅ Select all only for current page
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const pageUsernames = paginatedUsers.map((user) => user.Username);
      setSelectedUsers((prev) => [...new Set([...prev, ...pageUsernames])]);
    } else {
      const pageUsernames = paginatedUsers.map((user) => user.Username);
      setSelectedUsers((prev) =>
        prev.filter((username) => !pageUsernames.includes(username))
      );
    }
  };

  const isAllSelected =
    paginatedUsers.length > 0 &&
    paginatedUsers.every((user) => selectedUsers.includes(user.Username));

  const isIndeterminate =
    paginatedUsers.some((user) => selectedUsers.includes(user.Username)) &&
    !isAllSelected;

  const loadRoles = async () => {
    try {
      const response = await api.get("/roles");
      setAvailableRoles(response.data);
    } catch (err) {
      console.error("Failed to fetch roles", err);
    }
  };

  const handleEditUser = (user) => {
    setFormData({
      ...user,
      Roles: user.Roles?.map((r) => r.RoleID) || [],
    });
    setFormMode("edit");
    setShowForm(true);
  };

  const handleSubmitUser = async () => {
  try {
    if (formMode === "edit") {
      await api.put(`/users/${formData.UserID}`, formData);
    } else {
      if (!formData.Password || !formData.Username) {
        alert("⚠️ Username and password are required.");
        return;
      }

      await api.post("/addUser", {
        username: formData.Username,
        password: formData.Password,
        email: formData.Email || "default@example.com",
        name: formData.Name || "",
        lastname: formData.Lastname || "",
        roles: [3], // always role 3
        sendInvitationEmail: formData.SendInvite || false,
        associatedAccounts: formData.AssociatedAccounts || [],
      });
    }

    setShowForm(false);
    loadUsers();
  } catch (error) {
    // Log full error for debugging
    console.error("❌ Error submitting user:", error);

    // Extract more useful info
    const message =
      error.response?.data?.message || // backend JSON { message: "..." }
      error.response?.statusText ||   // HTTP status text (e.g. "Bad Request")
      error.message ||                // Axios error message
      "Unknown error occurred.";

    alert(`Failed to submit user:\n${message}`);
  }
};

  const handleResetPassword = (username) => {
    setUserToReset(username);
    setShowResetDialog(true);
  };

  const confirmResetPassword = async () => {
    try {
      const res = await api.post(`/users/${userToReset}/resetPassword`);
      setResetResult({
        username: userToReset,
        password: res.data.message?.split(": ")[1],
      });
    } catch (err) {
      console.error("Failed to reset password:", err);
      alert("Failed to reset password.");
    } finally {
      setShowResetDialog(false);
    }
  };

  const handleDeleteUser = (username) => {
    setUserToDelete(username);
    setShowDeleteDialog(true);
  };

  const handleSelectUser = (username) => {
    setSelectedUsers((prev) =>
      prev.includes(username)
        ? prev.filter((u) => u !== username)
        : [...prev, username]
    );
  };

  // pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, width: "100%", overflowX: "auto" }}>
      {/* Header Controls */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <TextField
          label="Search Users"
          size="small"
          fullWidth
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ maxWidth: 400 }}
        />

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setFormData({
                Username: "",
                Password: "",
                Name: "",
                Lastname: "",
                Email: "",
                SendInvite: true,
                AssociatedAccounts: [],
              });
              setFormMode("add");
              setShowForm(true);
            }}
          >
            New User
          </Button>
          <BulkInviteButton
            selectedUsers={selectedUsers}
            onSuccess={() => console.log("Invites sent!")}
          />
        </Box>
      </Box>

      {/* Table Section */}
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: "calc(100vh - 300px)",
          width: "100%",
          overflowX: "auto",
          overflowY: "auto",
          borderRadius: 2,
          boxShadow: 1,
        }}
      >
        <Table stickyHeader size="small" sx={{ minWidth: "max-content", width: "100%" }}>
          <TableHead>
            <TableRow>
              <TableCell
                padding="checkbox"
                sx={{
                  backgroundColor: "#007bff",
                  color: "#fff",
                  textAlign: "center",
                  width: 50,
                }}
              >
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={isIndeterminate}
                  onChange={handleSelectAll}
                  sx={{
                    color: "#fff",
                    "&.Mui-checked": { color: "#fff" },
                    p: 0.5,
                  }}
                />
              </TableCell>
              {[
                "Username",
                "Name",
                "Lastname",
                "Email",
                "Associated Accounts",
                "Status",
                "Actions",
              ].map((col) => (
                <TableCell
                  key={col}
                  sx={{
                    fontWeight: "bold",
                    backgroundColor: "#007bff",
                    color: "#fff",
                    whiteSpace: "nowrap",
                    maxWidth: 200,
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                  }}
                >
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedUsers.map((user) => (
              <TableRow key={user.Username} hover>
                <TableCell padding="checkbox" sx={{ textAlign: "center" }}>
                  <Checkbox
                    checked={selectedUsers.includes(user.Username)}
                    onChange={() => handleSelectUser(user.Username)}
                    color="primary"
                    sx={{ p: 0.5 }}
                  />
                </TableCell>
                <TableCell>{user.Username}</TableCell>
                <TableCell>{user.Name}</TableCell>
                <TableCell>{user.Lastname}</TableCell>
                <TableCell>{user.Email}</TableCell>
                <TableCell>
                  {user.AssociatedAccounts?.join(", ") || "None"}
                </TableCell>
                <TableCell>
                  <Typography
                    color={
                      user.Status?.toLowerCase() === "active" ? "green" : "red"
                    }
                  >
                    {user.Status}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <UserActions
                      user={user}
                      onEdit={handleEditUser}
                      onResetPassword={handleResetPassword}
                      onDelete={handleDeleteUser}
                    />
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={filteredUsers.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[50, 100, 200]}
      />

      {/* Dialogs */}
      <UserFormDialog
        open={showForm}
        mode={formMode}
        formData={formData}
        setFormData={setFormData}
        onClose={() => setShowForm(false)}
        onSubmit={handleSubmitUser}
        reloadUsers={loadUsers}
        availableRoles={availableRoles}
        showAssociatedAccount={true}
        hideRoleSelection={true}
      />

      <UserResetPasswordDialog
        open={showResetDialog}
        username={userToReset}
        onClose={() => setShowResetDialog(false)}
        onConfirm={confirmResetPassword}
      />

      <UserResetSuccessDialog
        open={!!resetResult}
        username={resetResult?.username}
        password={resetResult?.password}
        onClose={() => setResetResult(null)}
      />

      <UserDeleteDialog
        open={showDeleteDialog}
        username={userToDelete}
        onClose={() => setShowDeleteDialog(false)}
        onDelete={async () => {
          try {
            await api.put(`/users/${userToDelete}/deactivate`);
            setShowDeleteDialog(false);
            loadUsers();
          } catch (err) {
            console.error("Failed to deactivate user:", err);
            alert("Failed to deactivate user.");
          }
        }}
      />
    </Box>
  );
};

export default PropertyAccountsTable;
